import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Fuel, Navigation, Clock, Shield, Zap, MapPin, CheckCircle, Network, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { infrastructure, company } from '@data/company'
import AnimatedSection from '@components/AnimatedSection'

const capabilityIconMap: Record<string, React.ElementType> = {
  Fuel, Navigation, Clock, Shield, Zap, Network
}

const zoneColors = [
  'border-l-martimma-red',
  'border-l-martimma-yellow',
  'border-l-martimma-red',
  'border-l-martimma-yellow',
  'border-l-martimma-red',
  'border-l-martimma-yellow',
]

// Add your filling station photos to public/images/ using these filenames.
// The gallery shows all images that load successfully; it hides missing ones automatically.
const galleryImages = [
  { src: '/images/station-gallery-1.jpg', caption: 'Forecourt Operations' },
  { src: '/images/station-gallery-2.jpg', caption: 'Pump Island' },
  { src: '/images/station-gallery-3.jpg', caption: 'Customer Service' },
  { src: '/images/station-gallery-4.jpg', caption: 'Bulk Loading Bay' },
  { src: '/images/station-gallery-5.jpg', caption: 'Night Operations' },
  { src: '/images/station-gallery-6.jpg', caption: 'Safety Compliance' },
]

function StationImage({ src, alt, accent }: { src: string; alt: string; accent: 'red' | 'yellow' }) {
  const [loaded, setLoaded] = useState(false)
  const [errored, setErrored] = useState(false)

  return (
    <div className="relative h-52 overflow-hidden rounded-t-2xl">
      {/* Gradient placeholder — always renders, image sits on top */}
      <div className={`absolute inset-0 bg-gradient-to-br ${
        accent === 'red'
          ? 'from-martimma-red/30 via-martimma-dark-light to-martimma-dark'
          : 'from-martimma-yellow/20 via-martimma-dark-light to-martimma-dark'
      } flex items-center justify-center`}>
        {(errored || !loaded) && (
          <Fuel className={`w-12 h-12 ${accent === 'red' ? 'text-martimma-red/30' : 'text-martimma-yellow/30'}`} />
        )}
      </div>

      {/* Actual photo */}
      {!errored && (
        <img
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
        />
      )}

      {/* Bottom gradient so card content blends in */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-martimma-dark-card to-transparent" />
    </div>
  )
}

function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: { src: string; caption: string }[]
  startIndex: number
  onClose: () => void
}) {
  const [idx, setIdx] = useState(startIndex)

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length)
  const next = () => setIdx((i) => (i + 1) % images.length)

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); prev() }}
        className="absolute left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5 text-white" />
      </button>

      <motion.div
        key={idx}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[idx].src}
          alt={images[idx].caption}
          className="w-full max-h-[80vh] object-contain rounded-xl"
        />
        <p className="text-center text-gray-400 text-sm mt-3">{images[idx].caption}</p>
      </motion.div>

      <button
        onClick={(e) => { e.stopPropagation(); next() }}
        className="absolute right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5 text-white" />
      </button>
    </motion.div>
  )
}

export default function InfrastructurePage() {
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [visibleGallery, setVisibleGallery] = useState<{ src: string; caption: string }[]>([])

  const handleGalleryLoad = (img: { src: string; caption: string }) => {
    setVisibleGallery((prev) =>
      prev.find((i) => i.src === img.src) ? prev : [...prev, img]
    )
  }

  return (
    <>
      {/* Page Header */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-martimma-red/10 to-transparent" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-martimma-yellow/5 rounded-full blur-[100px]" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <AnimatedSection>
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Infrastructure</span>
            <h1 className="heading-xl mt-4 mb-6">{infrastructure.headline}</h1>
            <p className="body-lg max-w-3xl">{infrastructure.description}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Filling Stations with photos */}
      <section className="py-20 relative">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="mb-14">
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">Our Stations</span>
            <h2 className="heading-lg mt-3 mb-4">Filling Stations Across Nigeria</h2>
            <p className="body-lg max-w-2xl text-gray-400">
              We operate DPR-licensed retail service stations in three strategic locations,
              serving both walk-in retail customers and bulk industrial clients.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {infrastructure.stations.map((station, index) => {
              const accent = index % 2 === 0 ? 'red' : 'yellow'
              return (
                <AnimatedSection key={station.city} delay={index * 0.12}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="glass-card overflow-hidden h-full flex flex-col group"
                  >
                    {/* Station photo */}
                    <StationImage
                      src={station.image}
                      alt={`Martimma filling station — ${station.city}`}
                      accent={accent}
                    />

                    {/* Card content */}
                    <div className="p-7 flex flex-col flex-1">
                      {/* Zone badge + type */}
                      <div className="flex items-center justify-between mb-5">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                          accent === 'red'
                            ? 'bg-martimma-red/10 text-martimma-red border border-martimma-red/20'
                            : 'bg-martimma-yellow/10 text-martimma-yellow border border-martimma-yellow/20'
                        }`}>
                          {station.zone}
                        </span>
                        <span className="text-xs text-gray-500">{station.type}</span>
                      </div>

                      {/* City heading */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          accent === 'red' ? 'bg-martimma-red/10' : 'bg-martimma-yellow/10'
                        }`}>
                          <Fuel className={`w-5 h-5 ${accent === 'red' ? 'text-martimma-red' : 'text-martimma-yellow'}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-xl leading-tight">{station.city}</h3>
                          <p className="text-gray-400 text-sm">{station.state}</p>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="flex items-start gap-2 mb-5 pb-5 border-b border-white/5">
                        <MapPin className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                        <p className="text-gray-400 text-sm leading-relaxed">{station.address}</p>
                      </div>

                      {/* Services */}
                      <div className="flex-1 space-y-2">
                        {station.highlights.map((item) => (
                          <div key={item} className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 shrink-0 ${
                              accent === 'red' ? 'text-martimma-red/70' : 'text-martimma-yellow/70'
                            }`} />
                            <span className="text-gray-300 text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      {/* Contact */}
                      <div className="mt-5 pt-4 border-t border-white/5">
                        <p className="text-gray-500 text-xs">{company.contact.phone}</p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatedSection>
              )
            })}
          </div>
        </div>
      </section>

      {/* Station Photo Gallery */}
      <section className="py-20 bg-martimma-dark-light relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-martimma-red/30 to-transparent" />
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Gallery</span>
            <h2 className="heading-lg mt-3 mb-4">A Look Inside Our Stations</h2>
            <p className="text-gray-400 max-w-xl mx-auto text-base">
              A glimpse into the quality, cleanliness, and professionalism that defines every Martimma service station.
            </p>
          </AnimatedSection>

          {/* Hidden image preloaders — track which gallery images actually exist */}
          <div className="hidden">
            {galleryImages.map((img) => (
              <img
                key={img.src}
                src={img.src}
                alt=""
                onLoad={() => handleGalleryLoad(img)}
              />
            ))}
          </div>

          {visibleGallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {visibleGallery.map((img, i) => (
                <AnimatedSection key={img.src} delay={i * 0.07}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setLightbox(i)}
                    className={`relative w-full overflow-hidden rounded-2xl cursor-zoom-in ${
                      i === 0 ? 'md:col-span-2 aspect-[16/9]' : 'aspect-square'
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.caption}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-martimma-dark/0 hover:bg-martimma-dark/40 transition-colors duration-300 flex items-end p-4">
                      <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 translate-y-2 hover:translate-y-0 transition-all">
                        {img.caption}
                      </span>
                    </div>
                  </motion.button>
                </AnimatedSection>
              ))}
            </div>
          ) : (
            /* Placeholder grid shown until photos are added */
            <AnimatedSection>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { span: 'md:col-span-2 aspect-[16/9]', label: 'Station Overview' },
                  { span: 'aspect-square', label: 'Pump Area' },
                  { span: 'aspect-square', label: 'Forecourt' },
                  { span: 'aspect-square', label: 'Service Bay' },
                  { span: 'aspect-square', label: 'Night View' },
                ].map((slot, i) => (
                  <div
                    key={i}
                    className={`${slot.span} glass-card flex flex-col items-center justify-center gap-3 rounded-2xl`}
                  >
                    <Fuel className="w-8 h-8 text-martimma-red/20" />
                    <p className="text-gray-600 text-sm">{slot.label}</p>
                    <p className="text-gray-700 text-xs">Add photo: station-gallery-{i + 1}.jpg</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox
            images={visibleGallery}
            startIndex={lightbox}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>

      {/* Depot Partnerships */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <AnimatedSection className="text-center mb-14">
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">Depot Network</span>
            <h2 className="heading-lg mt-3 mb-4">Strategic Depot Partnerships</h2>
            <p className="body-lg max-w-2xl mx-auto text-gray-400">
              {infrastructure.depotPartners.description}
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {infrastructure.depotPartners.zones.map((zone, index) => (
              <AnimatedSection key={zone.name} delay={index * 0.08}>
                <div className={`glass-card p-6 border-l-4 ${zoneColors[index]} h-full`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-2 h-2 rounded-full bg-martimma-yellow animate-pulse" />
                    <h3 className="text-white font-semibold">{zone.name}</h3>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed">{zone.depots}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection>
            <div className="mt-10 glass-card p-8 text-center border border-martimma-yellow/10">
              <div className="flex flex-wrap items-center justify-center gap-8">
                {[
                  { value: '6', label: 'Geopolitical Zones' },
                  { value: '3', label: 'Own Filling Stations' },
                  { value: '36', label: 'States Covered' },
                  { value: '24/7', label: 'Supply Availability' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-3xl font-bold text-martimma-yellow mb-1">{stat.value}</div>
                    <div className="text-gray-400 text-xs uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-24 bg-martimma-dark-light relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-martimma-red/20 to-transparent" />
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-14">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Capabilities</span>
            <h2 className="heading-lg mt-3">What Our Infrastructure Enables</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {infrastructure.capabilities.map((cap, index) => {
              const Icon = capabilityIconMap[cap.icon] || Fuel
              return (
                <AnimatedSection key={cap.title} delay={index * 0.08}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="glass-card p-7 flex items-start gap-5 group h-full"
                  >
                    <div className="w-12 h-12 rounded-xl bg-martimma-red/10 flex items-center justify-center shrink-0 group-hover:bg-martimma-red/20 transition-colors">
                      <Icon className="w-6 h-6 text-martimma-red" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">{cap.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{cap.description}</p>
                    </div>
                  </motion.div>
                </AnimatedSection>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-martimma-red/15 to-martimma-yellow/15" />
        <div className="absolute inset-0 bg-martimma-dark/80" />
        <div className="relative z-10 section-padding max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="heading-lg mb-4">Need Bulk or Retail Petroleum Supply?</h2>
            <p className="body-lg mb-8 text-gray-300">
              Whether you need retail fuel at one of our stations or a bulk supply agreement
              delivered anywhere in Nigeria, our team is ready to serve you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={`tel:${company.contact.phone}`} className="btn-primary flex items-center gap-2">
                <Fuel className="w-5 h-5" />
                Call Us Now
              </a>
              <a href={`mailto:${company.contact.email}`} className="btn-outline">
                Send an Enquiry
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
